
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProfileFormProps {
  username: string;
  setUsername: (value: string) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  bio: string;
  setBio: (value: string) => void;
  nationality: string;
  setNationality: (value: string) => void;
  creatorTypes: string[];
  setCreatorTypes: (value: string[]) => void;
  userRole: string;
  updateProfile: () => Promise<void>;
  handleUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCreatorTypeClick: (type: string) => void;
}

const NATIONALITIES = [
  "American",
  "Argentinian",
  "Australian",
  "Austrian",
  "Belgian",
  "Bosnian and Herzegovinian",
  "British",
  "Bulgarian",
  "Croatian",
  "Cypriot",
  "Czech",
  "Danish",
  "Dutch",
  "Estonian",
  "Finnish",
  "French",
  "German",
  "Greek",
  "Hungarian",
  "Icelandic",
  "Irish",
  "Italian",
  "Japanese",
  "Latvian",
  "Lithuanian",
  "Maltese",
  "Mexican",
  "Moldovan",
  "Montenegrin",
  "Norwegian",
  "Polish",
  "Portuguese",
  "Romanian",
  "Russian",
  "Serbian",
  "Slovak",
  "Slovenian",
  "South African",
  "South Korean",
  "Spanish",
  "Swedish",
  "Swiss",
  "Turkish",
  "Ukrainian",
  "Other"
];

const CREATOR_TYPES = [
  "BBQ Lovers",
  "Budget Cooks",
  "Chefs",
  "Health Enthusiasts",
  "Home Cooks",
  "Influencers",
  "Nutritionists",
  "Parents",
  "Students",
  "Vegetarians/Vegans",
];

export const ProfileForm = ({
  username,
  setUsername,
  firstName,
  setFirstName,
  bio,
  setBio,
  nationality,
  setNationality,
  creatorTypes,
  userRole,
  updateProfile,
  handleUsernameChange,
  handleCreatorTypeClick,
}: ProfileFormProps) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="username" className="text-sm font-medium">
          Username (letters, numbers, and underscores only)
        </label>
        <Input
          id="username"
          type="text"
          value={username}
          onChange={handleUsernameChange}
          placeholder="Enter username"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="firstName" className="text-sm font-medium">
          First Name
        </label>
        <Input
          id="firstName"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Enter your first name"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="nationality" className="text-sm font-medium">
          Nationality
        </label>
        <Select 
          value={nationality} 
          onValueChange={setNationality}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select your nationality" />
          </SelectTrigger>
          <SelectContent>
            {NATIONALITIES.map((nat) => (
              <SelectItem key={nat} value={nat}>
                {nat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(userRole === 'creator' || userRole === 'superadmin') && (
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Your Creator Type (max 2)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CREATOR_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => handleCreatorTypeClick(type)}
                className={`p-2 text-sm rounded-lg transition-colors ${
                  creatorTypes.includes(type)
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
                type="button"
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor="bio" className="text-sm font-medium">
          Bio
        </label>
        <Textarea
          id="bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={4}
          placeholder="Tell us about yourself"
        />
      </div>

      <Button onClick={updateProfile} className="w-full">
        Update Profile
      </Button>
    </div>
  );
};
